import React, { useEffect, useMemo, useRef, useState } from "react";
// material-ui
import { Button, Container } from "@mui/material";
import { CellMeasurer, CellMeasurerCache, List, WindowScroller } from "react-virtualized";

// components
import { Translate } from "orion-components/i18n";
import { getDir } from "orion-components/i18n/Config/selector";
import { useSelector } from "react-redux";
import ChangeRequest from "./ChangeRequest/ChangeRequest";
import groupBy from "lodash/groupBy";
import { groupOrder } from "../../utility";

const loadHistoryButtonStyle = {
	width: 163,
	height: 35,
	textAlign: "center",
	fontFamily: "Roboto",
	fontWeight: "medium",
	fontSize: "12px",
	lineHeight: "16px",
	letterSpacing: "0px",
	color: "#FFFFFF",
	opacity: 1
};

const ChangeRequestHistory = ({ styles, mediaQuery, history, loadHistory }) => {
	const { minWidth600Query, mixedQuery } = mediaQuery;

	const dir = useSelector(state => getDir(state));
	const containerRef = useRef(null);
	const listRef = useRef(null);
	const [listWidth, setListWidth] = useState(0);
	const [scrollElement, setScrollElement] = useState(null);

	const cache = useMemo(
		() =>
			new CellMeasurerCache({
				defaultHeight: 260,
				fixedWidth: true
			}),
		[history]
	);
	const historyRows = useMemo(() => {
		const groupedHistory = groupBy(history, "level");

		return groupOrder.reduce((rows, key) => {
			const data = groupedHistory[key];

			if (!data) {
				return rows;
			}

			rows.push({ type: "header", key });
			[...data]
				.sort((a, b) => new Date(b.requestedDate) - new Date(a.requestedDate))
				.forEach(item => {
					rows.push({ type: "item", item });
				});

			return rows;
		}, []);
	}, [history]);

	const getScrollParent = element => {
		let parent = element?.parentElement;

		while (parent) {
			const overflowY = window.getComputedStyle(parent).overflowY;

			if (overflowY === "auto" || overflowY === "scroll") {
				return parent;
			}

			parent = parent.parentElement;
		}

		return window;
	};

	useEffect(() => {
		const updateListWidth = () => {
			if (containerRef.current) {
				setListWidth(containerRef.current.getBoundingClientRect().width);
			}
		};

		updateListWidth();
		window.addEventListener("resize", updateListWidth);

		return () => {
			window.removeEventListener("resize", updateListWidth);
		};
	}, [history]);

	useEffect(() => {
		cache.clearAll();
		if (listRef.current) {
			listRef.current.recomputeRowHeights();
		}
	}, [cache, historyRows.length, listWidth]);

	useEffect(() => {
		if (containerRef.current) {
			setScrollElement(getScrollParent(containerRef.current));
		}
	}, [history]);

	const rowRenderer = ({ index, key, parent, style }) => {
		const row = historyRows[index];
		const rowStyle =
			row.type === "header"
				? { ...style, height: "auto", width: "100%" }
				: { ...style, width: "100%", padding: "0 12px 40px 12px", boxSizing: "border-box" };

		return (
			<CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
				{({ measure, registerChild }) => (
					<div ref={registerChild} style={rowStyle}>
						{row.type === "header" ? (
							<div style={styles.section}>
								<Translate value={`mainContent.changeRequestQueue.groups.${row.key}`} />
							</div>
						) : (
							<ChangeRequest
								styles={styles}
								dir={dir}
								item={row.item}
								canManage={true}
								measureRow={measure}
							/>
						)}
					</div>
				)}
			</CellMeasurer>
		);
	};

	return (
		<>
			{!history && (
				<div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
					<Button
						variant="contained"
						style={loadHistoryButtonStyle}
						onClick={loadHistory}
						data-testid="settings-app-src-change-request-queue-components-change-request-history-button"
					>
						<Translate value="sideBar.loadHistory" />
					</Button>
				</div>
			)}
			{history && (
				<>
					<header style={{ textAlign: "center", marginTop: 32 }}>
						<h2>
							<Translate value="mainContent.changeRequestQueue.History.Title" />
						</h2>
						<div className="b1-dark-gray" style={{ marginTop: 15 }}>
							<Translate value="mainContent.changeRequestQueue.History.titleText" />
						</div>
					</header>
					<Container
						ref={containerRef}
						component={"section"}
						style={{
							paddingLeft: 0,
							paddingRight: 0,
							paddingTop: mixedQuery ? 0 : minWidth600Query ? 8 : 16,
							paddingBottom: 20,
							...(mixedQuery
								? {
										boxAlign: "stretch",
										alignItems: "stretch",
										display: "flex",
										flexWrap: "wrap",
										marginLeft: -12,
										width: "calc(100% + 24px)"
									}
								: {})
						}}
					>
						{listWidth > 0 && scrollElement && (
							<WindowScroller scrollElement={scrollElement}>
								{({ height, isScrolling, onChildScroll, registerChild, scrollTop }) => (
									<div ref={registerChild}>
										<List
											ref={listRef}
											autoHeight
											deferredMeasurementCache={cache}
											height={height}
											isScrolling={isScrolling}
											onScroll={onChildScroll}
											overscanRowCount={3}
											rowCount={historyRows.length}
											rowHeight={cache.rowHeight}
											rowRenderer={rowRenderer}
											scrollTop={scrollTop}
											width={listWidth}
											data-testid="settings-app-src-change-request-queue-components-change-request-history-list"
										/>
									</div>
								)}
							</WindowScroller>
						)}
					</Container>
				</>
			)}
		</>
	);
};

export default ChangeRequestHistory;
